import React, { useState, useEffect } from 'react';
import './Scheduler.css';

const Scheduler = ({ schedule, onChange, errors = {} }) => {
  const [localSchedule, setLocalSchedule] = useState({
    startTime: '09:00',
    endTime: '18:00',
    intervalMinutes: 30,
    timezone: 'Asia/Seoul',
    weekdays: [1, 2, 3, 4, 5], // 월-금
    enabled: true,
    retryCount: 3,
    retryDelay: 60,
    skipDuplicates: true,
    ...schedule
  });

  useEffect(() => {
    onChange(localSchedule);
  }, [localSchedule, onChange]);

  const handleScheduleChange = (field, value) => {
    setLocalSchedule(prev => ({ ...prev, [field]: value }));
  };

  const handleWeekdayToggle = (day) => {
    setLocalSchedule(prev => ({
      ...prev,
      weekdays: prev.weekdays.includes(day)
        ? prev.weekdays.filter(d => d !== day)
        : [...prev.weekdays, day].sort()
    }));
  };

  const validateTimeRange = () => {
    const start = localSchedule.startTime;
    const end = localSchedule.endTime;
    if (start >= end) {
      return '종료 시간은 시작 시간보다 나중이어야 합니다.';
    }
    return '';
  };

  const validateInterval = () => {
    const interval = localSchedule.intervalMinutes;
    if (interval < 1) {
      return '실행 주기는 1분 이상이어야 합니다.';
    }
    if (interval > 1440) { // 24시간
      return '실행 주기는 1440분(24시간)을 초과할 수 없습니다.';
    }

    // 시작시간과 종료시간 차이를 분으로 계산
    const [startHour, startMin] = localSchedule.startTime.split(':').map(Number);
    const [endHour, endMin] = localSchedule.endTime.split(':').map(Number);
    const totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);

    if (interval > totalMinutes) {
      return '실행 주기가 설정된 시간 범위보다 깁니다.';
    }
    return '';
  };

  const calculateExecutionTimes = () => {
    if (!localSchedule.enabled || localSchedule.weekdays.length === 0) {
      return [];
    }

    const timeError = validateTimeRange();
    const intervalError = validateInterval();
    if (timeError || intervalError) {
      return [];
    }

    const times = [];
    const [startHour, startMin] = localSchedule.startTime.split(':').map(Number);
    const [endHour, endMin] = localSchedule.endTime.split(':').map(Number);

    let currentTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;

    while (currentTime < endTime) {
      const hours = Math.floor(currentTime / 60);
      const minutes = currentTime % 60;
      times.push(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
      currentTime += localSchedule.intervalMinutes;
    }

    return times.slice(0, 10); // 최대 10개만 미리보기
  };

  const weekdayNames = ['일', '월', '화', '수', '목', '금', '토'];
  const timezones = [
    { value: 'Asia/Seoul', label: '한국 표준시 (KST)' },
    { value: 'UTC', label: '협정 세계시 (UTC)' },
    { value: 'America/New_York', label: '미국 동부시간 (EST)' },
    { value: 'Europe/London', label: '영국 시간 (GMT)' }
  ];

  const timeError = validateTimeRange();
  const intervalError = validateInterval();
  const executionTimes = calculateExecutionTimes();

  return (
    <div className="scheduler-container">
      <div>
        <h3 className="scheduler-title">스케줄 설정</h3>
        <p className="scheduler-description">
          크롤링이 실행될 시간과 주기를 설정하세요. 설정된 요일과 시간 범위 내에서 지정된 주기로 실행됩니다.
        </p>
      </div>

      <div className="scheduler-sections">
        {/* 기본 설정 */}
        <div className="scheduler-section">
          <h4 className="scheduler-section-title">
            ⏰ 기본 설정
          </h4>

          <div className="scheduler-grid">
            <div className="scheduler-field">
              <label className="scheduler-field-label">실행 주기 (분)</label>
              <input
                type="number"
                value={localSchedule.intervalMinutes}
                onChange={(e) => handleScheduleChange('intervalMinutes', parseInt(e.target.value) || 1)}
                className="scheduler-field-input"
                min="1"
                max="1440"
                placeholder="30"
              />
              {intervalError && (
                <div className="scheduler-error">
                  <p className="error-message">{intervalError}</p>
                </div>
              )}
            </div>

            <div className="scheduler-field">
              <label className="scheduler-field-label">시간대</label>
              <select
                value={localSchedule.timezone}
                onChange={(e) => handleScheduleChange('timezone', e.target.value)}
                className="scheduler-field-select"
              >
                {timezones.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="scheduler-field">
            <label className="scheduler-field-label">실행 시간 범위</label>
            <div className="time-range">
              <input
                type="time"
                value={localSchedule.startTime}
                onChange={(e) => handleScheduleChange('startTime', e.target.value)}
                className="scheduler-field-input"
              />
              <span className="time-separator">~</span>
              <input
                type="time"
                value={localSchedule.endTime}
                onChange={(e) => handleScheduleChange('endTime', e.target.value)}
                className="scheduler-field-input"
              />
            </div>
            {timeError && (
              <div className="scheduler-error">
                <p className="error-message">{timeError}</p>
              </div>
            )}
          </div>
        </div>

        {/* 요일 선택 */}
        <div className="weekdays-section">
          <h4 className="scheduler-section-title">
            📅 실행 요일
          </h4>
          <div className="weekdays-grid">
            {weekdayNames.map((name, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleWeekdayToggle(index)}
                className={`weekday-btn ${localSchedule.weekdays.includes(index) ? 'selected' : ''}`}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        {/* 고급 설정 */}
        <div className="advanced-settings">
          <h4 className="scheduler-section-title">
            ⚙️ 고급 설정
          </h4>

          <div className="setting-item">
            <span className="setting-label">재시도 횟수</span>
            <input
              type="number"
              value={localSchedule.retryCount}
              onChange={(e) => handleScheduleChange('retryCount', parseInt(e.target.value) || 0)}
              className="scheduler-field-input"
              min="0"
              max="10"
              style={{ width: '80px' }}
            />
          </div>

          <div className="setting-item">
            <span className="setting-label">재시도 지연 시간 (초)</span>
            <input
              type="number"
              value={localSchedule.retryDelay}
              onChange={(e) => handleScheduleChange('retryDelay', parseInt(e.target.value) || 60)}
              className="scheduler-field-input"
              min="1"
              max="3600"
              style={{ width: '100px' }}
            />
          </div>

          <div className="setting-item">
            <div className="checkbox-group">
              <input
                type="checkbox"
                checked={localSchedule.skipDuplicates}
                onChange={(e) => handleScheduleChange('skipDuplicates', e.target.checked)}
                className="scheduler-checkbox"
              />
              <label className="checkbox-label">
                중복 실행 방지
              </label>
            </div>
          </div>

          <div className="setting-item">
            <div className="checkbox-group">
              <input
                type="checkbox"
                checked={localSchedule.enabled}
                onChange={(e) => handleScheduleChange('enabled', e.target.checked)}
                className="scheduler-checkbox"
              />
              <label className="checkbox-label">
                스케줄 활성화
              </label>
            </div>
          </div>
        </div>

        {/* 실행 시간 미리보기 */}
        {localSchedule.enabled && executionTimes.length > 0 && (
          <div className="execution-preview">
            <h4 className="preview-title">📋 실행 시간 미리보기</h4>
            <p className="preview-content">
              선택된 요일에 다음 시간들에 크롤링이 실행됩니다:
            </p>
            <div className="preview-times">
              {executionTimes.map((time, index) => (
                <span key={index} className="preview-time">{time}</span>
              ))}
              {executionTimes.length === 10 && (
                <span className="preview-time">...</span>
              )}
            </div>
          </div>
        )}

        {/* 도움말 */}
        <div className="scheduler-help">
          <div className="help-title">💡 스케줄 설정 도움말</div>
          <div className="help-text">
            • 실행 주기: 크롤링이 실행되는 간격 (분 단위)<br/>
            • 시간 범위: 크롤링이 활성화되는 시간대<br/>
            • 요일 선택: 크롤링이 실행될 요일들<br/>
            • 재시도: 실패 시 재시도 횟수와 지연 시간<br/>
            • 중복 방지: 이전 크롤링이 진행 중일 때 새 실행 건너뛰기
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scheduler;